import React, { useState, useCallback, useEffect } from "react";
import { ClipboardList, Lock, CheckCircle2, ArrowLeft, Check, X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Quiz } from "@/components/quiz/types";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useSession } from "@/hooks/useSession";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface QuizDisplayProps {
  quiz?: any;
  isLoading?: boolean;
  isAccessible?: boolean;
  progress?: number;
  quizId?: string;
  resourceId?: string;
  onBack?: () => void;
  onComplete?: () => void;
  onQuizUpdate?: (quizId: string, attempt: any, resourceId?: string) => Promise<void>;
  isPreview?: boolean;
  isLastSection?: boolean;
  quizCompleted?: boolean;
  quizAttempt?: any;
}

export function QuizDisplay({
  quiz,
  isLoading = false,
  isAccessible = true,
  progress = 0,
  quizId,
  resourceId,
  onBack,
  onComplete,
  onQuizUpdate,
  isPreview = false,
  isLastSection = false,
  quizCompleted = false,
  quizAttempt = null
}: QuizDisplayProps) {
  const [singleAnswers, setSingleAnswers] = useState<{ [key: string]: string }>({});
  const [multipleAnswers, setMultipleAnswers] = useState<{ [key: string]: string[] }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptResult, setAttemptResult] = useState<{score: number, passed: boolean} | null>(null);
  const [isUpdatingScore, setIsUpdatingScore] = useState(false);
  const [passingScore, setPassingScore] = useState<number>(quiz?.passing_score || 70);
  const { session } = useSession();
  const { data: profile } = useProfile(session?.user?.id);
  const isAdmin = profile?.role === 'admin';
  const { toast } = useToast();

  useEffect(() => {
    if (quiz) {
      setPassingScore(quiz.passing_score);
    }
  }, [quiz]);

  useEffect(() => {
    if ((quizCompleted || attemptResult?.passed || quizAttempt?.passed) && (quizAttempt || attemptResult) && quiz) {
      console.log('Processing completed quiz attempt:', quizAttempt || attemptResult);
      const attemptToProcess = quizAttempt || { 
        responses: Object.entries(singleAnswers).map(([questionId, answer]) => ({
          questionId,
          answer
        })).concat(
          Object.entries(multipleAnswers).map(([questionId, answers]) => ({
            questionId,
            answer: JSON.stringify(answers)
          }))
        ),
        score: attemptResult?.score || 0,
        passed: attemptResult?.passed || false
      };
      processQuizAttempt(attemptToProcess);
    }
  }, [quizCompleted, quizAttempt, quiz, attemptResult]);

  const processQuizAttempt = (attempt: any) => {
    if (!attempt || !attempt.responses) {
      console.log('No valid attempt data to process');
      return;
    }
    
    console.log('Processing attempt data:', attempt);
    
    const newSingleAnswers: { [key: string]: string } = {};
    const newMultipleAnswers: { [key: string]: string[] } = {};
    
    attempt.responses.forEach((response: any) => {
      const { questionId, answer } = response;
      
      if (typeof answer === 'string') {
        try {
          const parsed = JSON.parse(answer);
          if (Array.isArray(parsed)) {
            newMultipleAnswers[questionId] = parsed;
          } else {
            newSingleAnswers[questionId] = answer;
          }
        } catch {
          newSingleAnswers[questionId] = answer;
        }
      }
    });
    
    console.log('Processed quiz attempt answers:', { newSingleAnswers, newMultipleAnswers });
    
    setSingleAnswers(newSingleAnswers);
    setMultipleAnswers(newMultipleAnswers);
    
    if (attempt.score !== undefined) {
      setAttemptResult({
        score: attempt.score,
        passed: attempt.passed
      });
    }
  };

  const handleSingleAnswerChange = (questionId: string, answer: string) => {
    if (isAdmin || quizCompleted || quizAttempt?.passed) return;
    
    setSingleAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleMultipleAnswerChange = (questionId: string, option: string, checked: boolean) => {
    if (isAdmin || quizCompleted || quizAttempt?.passed) return;
    
    setMultipleAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      
      if (checked) {
        return {
          ...prev,
          [questionId]: [...currentAnswers, option].filter((value, index, self) => self.indexOf(value) === index)
        };
      } else {
        return {
          ...prev,
          [questionId]: currentAnswers.filter(item => item !== option)
        };
      }
    });
  };

  const handleSubmitQuiz = async () => {
    if (!quiz || !session?.user || isAdmin) return;

    try {
      setIsSubmitting(true);
      
      const profileCreated = await ensureUserProfile();
      if (!profileCreated) {
        toast({
          title: "Error",
          description: "There was a problem submitting your quiz. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      const result = calculateScore(singleAnswers, multipleAnswers, quiz);
      console.log('Quiz submission result:', result);
      
      const combinedResponses = [
        ...Object.entries(singleAnswers).map(([questionId, answer]) => ({
          questionId,
          answer
        })),
        ...Object.entries(multipleAnswers).map(([questionId, answers]) => ({
          questionId,
          answer: JSON.stringify(answers)
        }))
      ];
      
      const { data: attemptData, error: attemptError } = await supabase
        .from('quiz_attempts')
        .insert({
          quiz_id: quiz.id,
          trainee_id: session.user.id,
          started_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
          score: result.score,
          passed: result.passed,
          responses: combinedResponses
        })
        .select()
        .single();

      if (attemptError) throw attemptError;
      
      console.log('Saved quiz attempt:', attemptData);

      if (result.passed && onQuizUpdate) {
        await onQuizUpdate(quiz.id, attemptData, resourceId);
      } else if (result.passed) {
        console.log('Quiz passed, updating resource_shares');
        try {
          await supabase
            .from('resource_shares')
            .upsert({
              resource_id: resourceId,
              trainee_id: session.user.id,
              quiz_completed: true,
              progress: 100
            }, {
              onConflict: 'resource_id,trainee_id',
              ignoreDuplicates: false
            });
        } catch (shareError) {
          console.warn('Could not update resource_shares, but quiz was completed:', shareError);
        }
      }

      processQuizAttempt(attemptData);
      setAttemptResult(result);
      
      toast({
        title: result.passed ? "Congratulations!" : "Quiz Result",
        description: `You scored ${result.score}%. ${result.passed ? "You've successfully completed the training!" : "Please try again."}`,
        variant: result.passed ? "default" : "destructive",
      });

      if (result.passed && onComplete) {
        onComplete();
      }

    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast({
        title: "Error",
        description: "Failed to submit quiz. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateScore = (singleAnswers: { [key: string]: string }, multipleAnswers: { [key: string]: string[] }, quiz: Quiz): { score: number, passed: boolean } => {
    if (!quiz.quiz_questions) return { score: 0, passed: false };
    
    let correctCount = 0;
    
    quiz.quiz_questions.forEach(question => {
      if (isMultipleSelectQuestion(question.type)) {
        const userAnswer = multipleAnswers[question.id] || [];
        const correctAnswerArray = Array.isArray(question.correct_answer) 
          ? question.correct_answer 
          : JSON.parse(question.correct_answer);
        
        const isCorrect = 
          userAnswer.length === correctAnswerArray.length && 
          userAnswer.every(item => correctAnswerArray.includes(item)) &&
          correctAnswerArray.every(item => userAnswer.includes(item));
        
        if (isCorrect) correctCount++;
      } else {
        if (singleAnswers[question.id] === question.correct_answer) {
          correctCount++;
        }
      }
    });
    
    const score = Math.round((correctCount / quiz.quiz_questions.length) * 100);
    const passed = score >= quiz.passing_score;
    
    return { score, passed };
  };

  const ensureUserProfile = async (): Promise<boolean> => {
    if (!session?.user?.id) return false;
    
    try {
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', session.user.id)
        .maybeSingle();
        
      if (checkError) throw checkError;
      
      if (!existingProfile) {
        console.log('Creating profile for user:', session.user.id);
        
        const { data: userData } = await supabase.auth.getUser();
        const userMeta = userData?.user?.user_metadata || {};
        
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: session.user.id,
            email: session.user.email,
            company: userMeta.company || '',
            role: userMeta.role || 'trainee',
            subscription_tier: 'free',
            resources_used_this_month: 0,
            total_resources_created: 0
          });
            
        if (insertError) throw insertError;
      }
      
      return true;
    } catch (error) {
      console.error('Error ensuring user profile exists:', error);
      return false;
    }
  };

  const updatePassingScore = async (newScore: number) => {
    if (!quiz || !isAdmin) return;
    
    try {
      setIsUpdatingScore(true);
      
      const { error } = await supabase
        .from('quizzes')
        .update({ passing_score: newScore })
        .eq('id', quiz.id);
        
      if (error) throw error;
      
      setPassingScore(newScore);
      
      toast({
        title: "Success",
        description: "Passing score updated successfully",
      });
      
    } catch (error) {
      console.error('Error updating passing score:', error);
      toast({
        title: "Error",
        description: "Failed to update passing score",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingScore(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8 w-full">
        <div className="animate-spin h-6 w-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full mx-auto mb-2"></div>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (!isAccessible && !isAdmin) {
    return (
      <div className="py-8 bg-[#F1F0FB] rounded-lg p-8 w-full">
        <Lock className="h-12 w-12 mb-4 text-[#8B5CF6] opacity-80 mx-auto" />
        <p className="text-lg font-medium text-[#1A1F2C] text-center">Quiz Locked</p>
        <p className="text-sm mt-1 text-[#8E9196] text-center">Complete at least one training section to unlock this quiz</p>
        <div className="mt-6 max-w-md mx-auto">
          <div className="flex justify-between text-sm mb-2 text-[#8E9196]">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <Progress 
            value={progress} 
            className="w-full bg-white h-2 [&>div]:bg-[#8B5CF6]" 
          />
        </div>
      </div>
    );
  }

  if (!quiz) {
    return null;
  }

  const isQuizFinished = quizCompleted || attemptResult?.passed || quizAttempt?.passed;

  if (isQuizFinished && !isAdmin) {
    console.log('Displaying completed quiz with results:', { 
      quizCompleted, 
      attemptResult, 
      quizAttempt, 
      singleAnswers, 
      multipleAnswers 
    });
    
    return (
      <ScrollArea className="h-[calc(100vh-100px)]">
        <div className="p-6">
          <div className="bg-white rounded-xl border border-[#8B5CF6] p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold text-[#1A1F2C] mb-4">
              {isLastSection ? "Training Completed!" : "Quiz Completed!"}
            </h3>
            <p className="text-lg text-[#8E9196] mb-6">
              {isLastSection 
                ? "Congratulations! You've successfully completed this training module."
                : "Great job! You've completed this quiz section. Continue to the next section of your training."}
            </p>
            <div className="inline-block px-4 py-2 bg-green-50 rounded-full text-green-600 font-medium mb-8">
              {isLastSection ? "100% Complete" : "Section Complete"}
            </div>
            
            <div className="mb-8">
              <div className="text-xl font-bold text-[#8B5CF6] mb-2">Your Score: {attemptResult?.score || quizAttempt?.score || 0}%</div>
              <div className="mb-6 text-sm text-gray-600">Passing score: {quiz?.passing_score}%</div>
              
              <div className="space-y-6 text-left">
                {quiz?.quiz_questions?.map((question, index) => {
                  const isMultipleType = isMultipleSelectQuestion(question.type);
                  let isCorrect = false;
                  
                  if (isMultipleType) {
                    const userAnswers = multipleAnswers[question.id] || [];
                    const correctAnswers = Array.isArray(question.correct_answer) 
                      ? question.correct_answer 
                      : JSON.parse(question.correct_answer);
                    isCorrect = 
                      userAnswers.length === correctAnswers.length && 
                      userAnswers.every(item => correctAnswers.includes(item)) &&
                      correctAnswers.every(item => userAnswers.includes(item));
                  } else {
                    isCorrect = singleAnswers[question.id] === question.correct_answer;
                  }
                  
                  return (
                    <div key={question.id} className={`border rounded-xl p-4 ${isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                      <div className="flex items-start gap-3">
                        {isCorrect ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                        ) : (
                          <X className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                        )}
                        <div>
                          <p className="font-medium text-[#1A1F2C]">{index + 1}. {question.question}</p>
                          
                          <div className="mt-3 space-y-2 pl-1">
                            {question.options.map((option) => {
                              const isUserSelected = isMultipleType 
                                ? multipleAnswers[question.id]?.includes(option)
                                : singleAnswers[question.id] === option;
                                
                              const isCorrectAnswer = isMultipleType
                                ? (Array.isArray(question.correct_answer) 
                                    ? question.correct_answer.includes(option)
                                    : JSON.parse(question.correct_answer).includes(option))
                                : option === question.correct_answer;
                              
                              return (
                                <div key={option} className="flex items-center gap-2">
                                  {isMultipleType ? (
                                    <div className={`h-4 w-4 rounded border flex items-center justify-center ${
                                      isUserSelected
                                        ? isCorrectAnswer 
                                          ? 'bg-green-100 border-green-500'
                                          : 'bg-red-100 border-red-500'
                                        : isCorrectAnswer 
                                          ? 'border-green-500'
                                          : 'border-gray-300'
                                    }`}>
                                      {isUserSelected && (
                                        isCorrectAnswer 
                                          ? <Check className="h-3 w-3 text-green-500" />
                                          : <X className="h-3 w-3 text-red-500" />
                                      )}
                                    </div>
                                  ) : (
                                    <div className={`h-4 w-4 rounded-full border ${
                                      isUserSelected
                                        ? isCorrectAnswer 
                                          ? 'bg-green-100 border-green-500'
                                          : 'bg-red-100 border-red-500'
                                        : isCorrectAnswer 
                                          ? 'border-green-500'
                                          : 'border-gray-300'
                                    }`}>
                                      {(isUserSelected || isCorrectAnswer) && (
                                        <div className={`h-2 w-2 mx-auto mt-1 rounded-full ${
                                          isCorrectAnswer ? 'bg-green-500' : isUserSelected ? 'bg-red-500' : ''
                                        }`} />
                                      )}
                                    </div>
                                  )}
                                  <span className={`text-sm ${
                                    isUserSelected
                                      ? isCorrectAnswer 
                                        ? 'font-medium text-green-700'
                                        : 'font-medium text-red-700'
                                      : isCorrectAnswer
                                        ? 'font-medium text-green-700'
                                        : 'text-gray-600'
                                  }`}>
                                    {option}
                                    {isCorrectAnswer && !isUserSelected && " (Correct Answer)"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    );
  }

  const allQuestionsAnswered = quiz.quiz_questions?.every(question => {
    if (isMultipleSelectQuestion(question.type)) {
      return multipleAnswers[question.id]?.length > 0;
    } else {
      return singleAnswers[question.id] !== undefined;
    }
  });

  return (
    <ScrollArea className="h-[calc(100vh-100px)]">
      <div className="p-6">
        <div className="bg-white rounded-xl border border-[#E5DEFF] p-8 space-y-8 shadow-sm">
          {onBack && (
            <Button variant="ghost" size="sm" className="mb-4" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          
          <div>
            <h3 className="text-xl font-semibold text-[#1A1F2C]">{quiz?.title}</h3>
            {quiz?.description && (
              <p className="mt-2 text-[#8E9196]">{quiz.description}</p>
            )}
            <div className="flex justify-between items-center mt-3">
              {isAdmin ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#8B5CF6]">Passing score:</span>
                  <Select
                    value={passingScore?.toString()}
                    onValueChange={(value) => updatePassingScore(parseInt(value))}
                    disabled={isUpdatingScore}
                  >
                    <SelectTrigger className="h-8 w-20 text-xs bg-[#F1F0FB] border-[#E5DEFF] text-[#8B5CF6]">
                      <SelectValue placeholder="Score" />
                    </SelectTrigger>
                    <SelectContent>
                      {[50, 60, 70, 80, 90, 100].map((score) => (
                        <SelectItem key={score} value={score.toString()}>
                          {score}%
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <p className="text-sm inline-block px-3 py-1 bg-[#F1F0FB] rounded-full text-[#8B5CF6]">
                  Passing score: {quiz?.passing_score}%
                </p>
              )}
            </div>
          </div>
          
          <div className="space-y-6">
            <h4 className="text-md font-semibold text-[#1A1F2C]">Questions:</h4>
            {quiz?.quiz_questions?.map((question, index) => (
              <div key={question.id} className="border border-[#E5DEFF] rounded-xl p-6 space-y-4 bg-[#F8F7FD]">
                <p className="font-medium text-[#1A1F2C]">{index + 1}. {question.question}</p>
                
                {isAdmin ? (
                  <div className="pl-4 space-y-3">
                    {isMultipleSelectQuestion(question.type) ? (
                      <>
                        <div className="bg-amber-50 px-3 py-2 rounded text-sm text-amber-700 mb-2">
                          Multiple select question (Checkbox)
                        </div>
                        {question.options.map((option) => {
                          const correctAnswers = Array.isArray(question.correct_answer) 
                            ? question.correct_answer 
                            : JSON.parse(question.correct_answer);
                          const isCorrect = correctAnswers.includes(option);
                          
                          return (
                            <div key={option} className="flex items-center space-x-3">
                              <div className={`h-4 w-4 rounded border ${
                                isCorrect
                                  ? 'bg-green-100 border-green-500 flex items-center justify-center' 
                                  : 'bg-gray-100 border-gray-300'
                              }`}>
                                {isCorrect && <Check className="h-3 w-3 text-green-500" />}
                              </div>
                              <span className={`text-sm ${
                                isCorrect
                                  ? 'font-medium text-green-700' 
                                  : 'text-[#8E9196]'
                              }`}>
                                {option} {isCorrect && '(Correct Answer)'}
                              </span>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      question.options.map((option) => (
                        <div key={option} className="flex items-center space-x-3">
                          <div className={`h-4 w-4 rounded-full border ${
                            option === question.correct_answer 
                              ? 'bg-green-100 border-green-500' 
                              : 'bg-gray-100 border-gray-300'
                          }`}>
                            {option === question.correct_answer && (
                              <div className="h-2 w-2 mx-auto mt-1 rounded-full bg-green-500" />
                            )}
                          </div>
                          <span className={`text-sm ${
                            option === question.correct_answer 
                              ? 'font-medium text-green-700' 
                              : 'text-[#8E9196]'
                          }`}>
                            {option} {option === question.correct_answer && '(Correct Answer)'}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <>
                    {isMultipleSelectQuestion(question.type) ? (
                      <div className="pl-4 space-y-3">
                        <p className="text-sm text-[#8E9196] italic mb-2">Select all that apply</p>
                        {question.options.map((option) => {
                          const isChecked = multipleAnswers[question.id]?.includes(option) || false;
                          
                          return (
                            <div key={option} className="flex items-center space-x-3 p-2 hover:bg-[#E5DEFF] rounded-md transition-colors">
                              <Checkbox 
                                id={`${question.id}-${option}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  handleMultipleAnswerChange(question.id, option, checked === true);
                                }}
                                className="border-[#8B5CF6] text-[#8B5CF6]"
                              />
                              <Label 
                                htmlFor={`${question.id}-${option}`} 
                                className="text-[#1A1F2C] cursor-pointer w-full"
                              >
                                {option}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <RadioGroup
                        value={singleAnswers[question.id]}
                        onValueChange={(value) => handleSingleAnswerChange(question.id, value)}
                      >
                        <div className="pl-4 space-y-3">
                          {question.options.map((option) => (
                            <div key={option} className="flex items-center space-x-3 p-2 hover:bg-[#E5DEFF] rounded-md transition-colors">
                              <RadioGroupItem 
                                value={option} 
                                id={`${question.id}-${option}`} 
                                className="border-[#8B5CF6] text-[#8B5CF6]"
                              />
                              <Label 
                                htmlFor={`${question.id}-${option}`} 
                                className="text-[#1A1F2C] cursor-pointer w-full"
                              >
                                {option}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </RadioGroup>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-[#E5DEFF]">
            {!isAdmin ? (
              <Button
                onClick={handleSubmitQuiz}
                disabled={isSubmitting || !allQuestionsAnswered || quizCompleted || quizAttempt?.passed}
                className="w-full bg-[#8B5CF6] hover:bg-[#7C3AED] py-3 text-base font-medium"
              >
                {isSubmitting ? "Submitting..." : quizCompleted || quizAttempt?.passed ? "Quiz Already Completed" : "Submit Quiz"}
              </Button>
            ) : (
              <div className="w-full text-center py-2 px-4 bg-gray-100 rounded-md">
                <span className="text-sm text-gray-500">Admin preview mode - showing correct answers</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

function isMultipleSelectQuestion(questionType: string): boolean {
  return questionType === 'multiple_select';
}
